import { useState } from 'react';
import {
  useInvites,
  useCreateInvite,
  useRevokeInvite,
  useMembers,
} from '../../api/hooks/admin.hook';
import { usePlanGate } from '../../hooks/usePlanGate';
import { LimitBadge } from '../../components/PlanGate';

export const InvitesPage = () => {
  const { data, isLoading } = useInvites();
  const { data: membersData } = useMembers();
  const createInvite = useCreateInvite();
  const revokeInvite = useRevokeInvite();
  const { planInfo, isHardLimit } = usePlanGate();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [error, setError] = useState<string | null>(null);

  const invites = data?.invites ?? [];
  const memberCount = membersData?.members?.length ?? 0;
  const atLimit =
    planInfo.max_members !== null && memberCount >= planInfo.max_members;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    try {
      await createInvite.mutateAsync({ email: email.trim().toLowerCase(), role });
      setEmail('');
    } catch (err: unknown) {
      const apiErr = err as { error?: string; detail?: { error?: string } };
      const msg = apiErr?.detail?.error ?? apiErr?.error ?? 'Failed to send invitation.';
      if (msg === 'user_already_member') {
        setError('This user is already a member.');
      } else if (msg === 'member_limit_reached') {
        setError('Member limit reached. Upgrade your plan to invite more members.');
      } else {
        setError(msg);
      }
    }
  };

  return (
    <div className="max-w-4xl">
      <h1 className="text-lg font-semibold text-neutral-900 mb-6">Invitations</h1>

      {/* Member limit indicator */}
      {planInfo.max_members !== null && (
        <div className="flex items-center gap-2 mb-4 text-sm text-neutral-600">
          Members: <LimitBadge current={memberCount} limit={planInfo.max_members} />
          {atLimit && isHardLimit && (
            <span className="text-xs text-red-600 ml-2">
              Upgrade your plan to invite more members.
            </span>
          )}
        </div>
      )}

      {/* Invite form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg border border-neutral-200 p-4 mb-6"
      >
        <h2 className="text-sm font-medium text-neutral-700 mb-3">
          Invite a new member
        </h2>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs text-neutral-500 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              className="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            />
          </div>
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="border border-neutral-200 rounded px-3 py-1.5 text-sm bg-white"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={createInvite.isLoading || (atLimit && isHardLimit)}
            className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {createInvite.isLoading ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
        {error && (
          <p className="mt-2 text-xs text-red-600">{error}</p>
        )}
      </form>

      {/* Pending invites list */}
      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-neutral-100">
          <h2 className="text-sm font-medium text-neutral-700">
            Pending Invitations
            <span className="text-neutral-400 font-normal ml-1">
              ({invites.length})
            </span>
          </h2>
        </div>

        {isLoading ? (
          <div className="px-4 py-8 text-center text-sm text-neutral-400">
            Loading...
          </div>
        ) : invites.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-neutral-400">
            No pending invitations.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 uppercase tracking-wide border-b border-neutral-50">
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Sent</th>
                <th className="px-4 py-2">Expires</th>
                <th className="px-4 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr
                  key={invite.id}
                  className="border-b border-neutral-50 hover:bg-neutral-50/50"
                >
                  <td className="px-4 py-2.5 text-neutral-900">{invite.email}</td>
                  <td className="px-4 py-2.5 text-neutral-500 capitalize">
                    {invite.role}
                  </td>
                  <td className="px-4 py-2.5 text-neutral-500 text-xs">
                    {new Date(invite.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2.5 text-neutral-500 text-xs">
                    {invite.expires_at
                      ? new Date(invite.expires_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => revokeInvite.mutate(invite.id)}
                      disabled={revokeInvite.isLoading}
                      className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
